import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest, computeHubModalComboboxPanelBox, usePortalDropdownPosition } from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';
import { vsmeRequest } from './CabinetComplianceTab';
import type { CompaniesListResponse } from '../../../companies/src/types/company';
import { errorToUserFacingMessage } from '../api/apiErrors';

export type DiligencePackApiRow = {
  id: number;
  name: string;
  description: string | null;
  target_mode: 'all' | 'restricted';
  status: string;
  created_at: string;
  updated_at: string;
};

type DiligencePackItemApiRow = {
  id: number;
  pack_id: number;
  source_key: string;
  title: string;
  label?: string;
  description: string | null;
  attachments_description: string | null;
  step_key: string;
  disclosure_ids?: string[];
  sort_order: number;
};

type ItemFormRow = {
  formId: string;
  source_key: string;
  title: string;
  disclosure_ids: string[];
  description: string;
  attachments_description: string;
  sort_order: number;
};

type ItemTargetRow = { id: string; title: string; label?: string };

type ItemTargetsPayload = {
  criteria: ItemTargetRow[];
};

type FormState = {
  name: string;
  description: string;
  target_mode: 'all' | 'restricted';
  items: ItemFormRow[];
  targetCompanyIds: number[];
};

const generateSourceKey = (): string =>
  `custom:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 9)}`;

const generateFormId = (): string => Math.random().toString(36).slice(2, 10);

const emptyItem = (): ItemFormRow => ({
  formId: generateFormId(),
  source_key: generateSourceKey(),
  title: '',
  disclosure_ids: [],
  description: '',
  attachments_description: '',
  sort_order: 0,
});

const itemFromApi = (row: DiligencePackItemApiRow, idx: number): ItemFormRow => ({
  formId: generateFormId(),
  source_key: generateSourceKey(),
  title: row.title,
  disclosure_ids:
    Array.isArray(row.disclosure_ids) && row.disclosure_ids.length > 0
      ? row.disclosure_ids
      : [row.step_key.trim().toLowerCase()].filter((id) => id !== ''),
  description: row.description ?? '',
  attachments_description: row.attachments_description ?? '',
  sort_order: idx,
});

export type DiligencePackEditorModalProps = {
  mode: { kind: 'create' } | { kind: 'edit'; pack: DiligencePackApiRow };
  cabinetId: number;
  bootstrapKey: string;
  onClose: () => void;
  onSuccess: () => void;
};

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const TrashRowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Must exceed hub $z-modal (100000) — portal renders to body, not inside modal.
const COMBOBOX_Z = 100_001;

type ComboboxOption = { id: string; label: string };

type SearchableComboboxProps = {
  id: string;
  value: string;
  onChange: (id: string) => void;
  options: ComboboxOption[];
  disabled: boolean;
  placeholder?: string;
};

const SearchableCombobox = ({
  id,
  value,
  onChange,
  options,
  disabled,
  placeholder = __('— Sélectionner —'),
}: SearchableComboboxProps) => {
  const listboxId = `${id}-listbox`;
  const searchId = `${id}-search`;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const panelBox = usePortalDropdownPosition(open, triggerRef, computeHubModalComboboxPanelBox);

  const trimmed = query.trim().toLowerCase();

  const filteredOptions = useMemo(
    () =>
      trimmed === ''
        ? options
        : options.filter(
            (o) =>
              o.label.toLowerCase().includes(trimmed) || o.id.toLowerCase().includes(trimmed),
          ),
    [options, trimmed],
  );

  // Prepend unknown row when editing a value not in the current option list
  const rows = useMemo((): ComboboxOption[] => {
    const known = value !== '' && options.some((o) => o.id === value);
    if (value !== '' && !known && trimmed === '') {
      return [{ id: value, label: `${__('Valeur actuelle')}: ${value}` }, ...filteredOptions];
    }
    return filteredOptions;
  }, [filteredOptions, options, value, trimmed]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setHighlighted(0);
    }
  }, [open]);

  useEffect(() => {
    setHighlighted((h) => (rows.length === 0 ? 0 : Math.min(h, rows.length - 1)));
  }, [rows.length]);

  useEffect(() => {
    if (!open || !listRef.current) return undefined;
    document.getElementById(`${id}-row-${highlighted}`)?.scrollIntoView({ block: 'nearest' });
  }, [highlighted, open, id]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (ev: Event) => {
      const target = ev.target;
      if (!(target instanceof Node)) return;
      if (wrapperRef.current?.contains(target)) return;
      if (document.getElementById(`${id}-panel`)?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, id]);

  const pick = useCallback(
    (optId: string) => {
      onChange(optId);
      setOpen(false);
      setQuery('');
      window.setTimeout(() => triggerRef.current?.focus(), 0);
    },
    [onChange],
  );

  const onKeyDownPanel = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, rows.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      const row = rows[highlighted];
      if (row) {
        e.preventDefault();
        pick(row.id);
      }
    }
  };

  const selectedLabel = options.find((o) => o.id === value)?.label ?? (value !== '' ? value : '');

  const panelNode =
    open && panelBox && typeof document !== 'undefined'
      ? createPortal(
          <div
            id={`${id}-panel`}
            className="hub-combobox__panel"
            role="presentation"
            style={{
              position: 'fixed',
              top: panelBox.top,
              left: panelBox.left,
              width: panelBox.width,
              zIndex: COMBOBOX_Z,
            }}
            onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
          >
            <div
              role="none"
              className="hub-combobox__panel-inner"
              onKeyDown={onKeyDownPanel}
            >
              <label htmlFor={searchId} className="visually-hidden">
                {__('Filtrer')}
              </label>
              <input
                id={searchId}
                ref={searchRef}
                type="search"
                className="hub-combobox__search"
                value={query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setQuery(e.target.value);
                  setHighlighted(0);
                }}
                placeholder={__('Rechercher…')}
                autoComplete="off"
                aria-controls={listboxId}
                aria-activedescendant={rows.length > 0 ? `${id}-row-${highlighted}` : undefined}
              />
              <ul
                ref={listRef}
                id={listboxId}
                role="listbox"
                className="hub-combobox__list"
                aria-label={__('Options')}
              >
                {rows.map((row, idx) => {
                  const sel = value === row.id;
                  const hi = highlighted === idx;
                  return (
                    <li key={row.id} role="presentation">
                      <button
                        type="button"
                        id={`${id}-row-${idx}`}
                        role="option"
                        aria-selected={sel}
                        className={`hub-combobox__option${sel ? ' hub-combobox__option--selected' : ''}${hi ? ' hub-combobox__option--highlight' : ''}`}
                        onClick={() => pick(row.id)}
                      >
                        {row.label}
                      </button>
                    </li>
                  );
                })}
                {trimmed !== '' && rows.length === 0 && (
                  <li className="hub-combobox__empty" role="presentation">
                    {__('Aucun résultat.')}
                  </li>
                )}
              </ul>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div ref={wrapperRef} className="hub-combobox">
        <button
          ref={triggerRef}
          id={id}
          type="button"
          className="hub-combobox__trigger"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          onClick={() => {
            if (!disabled) setOpen((o) => !o);
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (!disabled) setOpen((o) => !o);
            }
            if (e.key === 'ArrowDown' && !open) {
              e.preventDefault();
              setOpen(true);
            }
          }}
        >
          <span
            className={
              selectedLabel
                ? 'hub-combobox__value'
                : 'hub-combobox__placeholder'
            }
          >
            {selectedLabel || placeholder}
          </span>
          <span className="hub-combobox__chevron" aria-hidden>
            {open ? '▲' : '▼'}
          </span>
        </button>
      </div>
      {panelNode}
    </>
  );
};

type DisclosureSetPickerProps = {
  id: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: ComboboxOption[];
  disabled: boolean;
};

const DisclosureSetPicker = ({
  id,
  values,
  onChange,
  options,
  disabled,
}: DisclosureSetPickerProps) => {
  const available = options.filter((option) => !values.includes(option.id));

  return (
    <fieldset className="pack-editor-modal__fieldset">
      <legend className="visually-hidden">{__('Points de donnée sélectionnés')}</legend>
      <SearchableCombobox
        id={`${id}-add`}
        value=""
        onChange={(value) => onChange([...values, value])}
        options={available}
        disabled={disabled || available.length === 0}
        placeholder={__('Ajouter un point de donnée')}
      />
      {values.length > 0 && (
        <div className="pack-editor-modal__company-list">
          {values.map((value) => {
            const label = options.find((option) => option.id === value)?.label ?? value;
            return (
              <label key={value} className="pack-editor-modal__company-row">
                <input
                  type="checkbox"
                  name={`${id}[]`}
                  value={value}
                  checked
                  onChange={() => onChange(values.filter((idValue) => idValue !== value))}
                  disabled={disabled}
                />
                {label}
              </label>
            );
          })}
        </div>
      )}
    </fieldset>
  );
};

const DiligencePackEditorModal = ({
  mode,
  cabinetId,
  bootstrapKey,
  onClose,
  onSuccess,
}: DiligencePackEditorModalProps) => {
  const isEdit = mode.kind === 'edit';
  const pack = isEdit ? mode.pack : null;

  const [itemsLoadState, setItemsLoadState] = useState<'loading' | 'ready' | 'error'>(
    isEdit ? 'loading' : 'ready',
  );
  const [itemsLoadError, setItemsLoadError] = useState('');

  const [form, setForm] = useState<FormState>({
    name: pack?.name ?? '',
    description: pack?.description ?? '',
    target_mode: pack?.target_mode ?? 'all',
    items: [],
    targetCompanyIds: [],
  });

  const [submitState, setSubmitState] = useState<'idle' | 'saving' | 'error'>('idle');
  const [submitError, setSubmitError] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);

  const [itemTargets, setItemTargets] = useState<ItemTargetsPayload | null>(null);
  const [itemTargetsLoad, setItemTargetsLoad] = useState<'idle' | 'loading' | 'ready' | 'error'>('loading');

  const { data: companiesData } = useQuery({
    queryKey: ['compliance-modal-companies'],
    queryFn: () => apiRequest<CompaniesListResponse>('companies?per_page=100', {}, bootstrapKey),
    staleTime: 60_000,
  });
  const allCompanies = companiesData?.companies ?? [];

  useEffect(() => {
    let cancelled = false;
    setItemTargetsLoad('loading');
    void (async () => {
      try {
        const res = await vsmeRequest<{ success: boolean; data: ItemTargetsPayload }>(
          'GET',
          'diligence-packs/item-targets',
        );
        if (!cancelled) {
          setItemTargets(res.data ?? { criteria: [] });
          setItemTargetsLoad('ready');
        }
      } catch {
        if (!cancelled) {
          setItemTargets(null);
          setItemTargetsLoad('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit || pack === null) return;

    const load = async () => {
      setItemsLoadState('loading');
      try {
        const [itemsRes, targetsRes] = await Promise.all([
          vsmeRequest<{ success: boolean; data: { items: DiligencePackItemApiRow[] } }>(
            'GET',
            `diligence-packs/${pack.id}/items`,
          ),
          vsmeRequest<{ success: boolean; data: { targets: Array<{ target_type: string; target_id: number }> } }>(
            'GET',
            `diligence-packs/${pack.id}/targets`,
          ),
        ]);

        const items = itemsRes.data?.items ?? [];
        const targets = targetsRes.data?.targets ?? [];

        setForm((prev) => ({
          ...prev,
          items: items.map((it, idx) => itemFromApi(it, idx)),
          targetCompanyIds: targets
            .filter((t) => t.target_type === 'company')
            .map((t) => t.target_id),
        }));
        setItemsLoadState('ready');
      } catch (e) {
        setItemsLoadError(errorToUserFacingMessage(e, __('Chargement des diligences impossible.')));
        setItemsLoadState('error');
      }
    };

    void load();
  }, [isEdit, pack]);

  const formReady = itemsLoadState === 'ready' && itemTargetsLoad === 'ready' && itemTargets !== null;

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addItem = () => {
    const item = emptyItem();
    setForm((prev) => ({ ...prev, items: [item, ...prev.items] }));
  };

  const removeItem = (formId: string) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((it) => it.formId !== formId),
    }));
  };

  const updateItem = (formId: string, key: keyof ItemFormRow, value: string | number) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it) => (it.formId === formId ? { ...it, [key]: value } : it)),
    }));
  };

  const updateItemDisclosureIds = (formId: string, disclosureIds: string[]) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it) =>
        it.formId === formId ? { ...it, disclosure_ids: disclosureIds } : it,
      ),
    }));
  };

  const toggleCompany = (id: number) => {
    setForm((prev) => ({
      ...prev,
      targetCompanyIds: prev.targetCompanyIds.includes(id)
        ? prev.targetCompanyIds.filter((c) => c !== id)
        : [...prev.targetCompanyIds, id],
    }));
  };

  const buildPayload = () => {
    const targets =
      form.target_mode === 'restricted'
        ? form.targetCompanyIds.map((id) => ({ target_type: 'company', target_id: id }))
        : [];

    return {
      name: form.name.trim(),
      description: form.description.trim() || null,
      target_mode: form.target_mode,
      meta: null,
      items: form.items.map((it, idx) => ({
        source_key: it.source_key,
        title: it.title.trim(),
        disclosure_ids: it.disclosure_ids,
        description: it.description.trim() || null,
        attachments_description: it.attachments_description.trim() || null,
        sort_order: idx,
      })),
      targets,
    };
  };

  const validate = (): string | null => {
    if (form.name.trim() === '') return __('Le nom est obligatoire.');
    if (form.target_mode === 'restricted' && form.targetCompanyIds.length === 0) {
      return __('Sélectionnez au moins un dossier pour ce mode.');
    }
    for (const [i, it] of form.items.entries()) {
      if (it.title.trim() === '') return `${__('Ligne')} ${i + 1} : ${__('le titre est obligatoire.')}`;
      if (it.disclosure_ids.length === 0) return `${__('Ligne')} ${i + 1} : ${__('la cible est obligatoire.')}`;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err !== null) {
      setSubmitError(err);
      return;
    }
    setSubmitState('saving');
    setSubmitError('');
    try {
      const payload = buildPayload();
      if (pack !== null) {
        await vsmeRequest('PUT', `diligence-packs/${pack.id}`, payload);
      } else {
        await vsmeRequest('POST', `cabinets/${cabinetId}/diligence-packs`, payload);
      }
      onSuccess();
    } catch (e) {
      setSubmitError(errorToUserFacingMessage(e, __('Erreur lors de l\'enregistrement.')));
      setSubmitState('error');
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  const title = isEdit ? __('Modifier le pack') : __('Nouveau pack de diligences');
  const saving = submitState === 'saving';

  return (
    <div
      className="thn-modal-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="pack-editor-modal-title"
    >
      <div className="thn-modal thn-modal--large pack-editor-modal">
        <button
          type="button"
          className="thn-modal__close"
          onClick={onClose}
          aria-label={__('Fermer')}
        >
          <CloseIcon />
        </button>

        <div className="thn-modal__header">
          <h3 id="pack-editor-modal-title">{title}</h3>
        </div>

        {itemsLoadState === 'loading' && (
          <div className="thn-modal__body">
            <p className="pack-editor-modal__hint">{__('Chargement…')}</p>
          </div>
        )}

        {itemsLoadState === 'error' && (
          <div className="thn-modal__body">
            <p className="pack-editor-modal__error">{itemsLoadError}</p>
          </div>
        )}

        {itemsLoadState === 'ready' && itemTargetsLoad === 'loading' && (
          <div className="thn-modal__body">
            <p className="pack-editor-modal__hint">{__('Chargement des points de donnée…')}</p>
          </div>
        )}

        {itemsLoadState === 'ready' && itemTargetsLoad === 'error' && (
          <div className="thn-modal__body">
            <p className="pack-editor-modal__error">
              {__('Impossible de charger la liste des points de donnée.')}
            </p>
          </div>
        )}

        {formReady && (
          <form onSubmit={(e) => { void handleSubmit(e); }}>
            <div className="thn-modal__body pack-editor-modal__body">

              <div className="pack-editor-modal__meta-section">
                <div className="pack-editor-modal__field">
                  <label htmlFor="pack-name" className="pack-editor-modal__label">
                    {__('Nom')}<span aria-hidden="true"> *</span>
                  </label>
                  <input
                    id="pack-name"
                    type="text"
                    className="pack-editor-modal__input"
                    value={form.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('name', e.target.value)}
                    maxLength={191}
                    required
                    disabled={saving}
                  />
                </div>

                <div className="pack-editor-modal__field">
                  <label htmlFor="pack-description" className="pack-editor-modal__label">
                    {__('Description')}
                  </label>
                  <textarea
                    id="pack-description"
                    className="pack-editor-modal__textarea"
                    rows={2}
                    value={form.description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setField('description', e.target.value)}
                    disabled={saving}
                  />
                </div>

                <fieldset className="pack-editor-modal__fieldset">
                  <legend className="pack-editor-modal__label">{__('Applicabilité')}</legend>
                  <div className="pack-editor-modal__radio-row">
                    <label className="pack-editor-modal__radio-label">
                      <input
                        type="radio"
                        name="target_mode"
                        value="all"
                        checked={form.target_mode === 'all'}
                        onChange={() => setField('target_mode', 'all')}
                        disabled={saving}
                      />
                      {__('Tous les dossiers')}
                    </label>
                    <label className="pack-editor-modal__radio-label">
                      <input
                        type="radio"
                        name="target_mode"
                        value="restricted"
                        checked={form.target_mode === 'restricted'}
                        onChange={() => setField('target_mode', 'restricted')}
                        disabled={saving}
                      />
                      {__('Dossiers sélectionnés')}
                    </label>
                  </div>
                </fieldset>

                {form.target_mode === 'restricted' && (
                  <div className="pack-editor-modal__field">
                    <span className="pack-editor-modal__label">{__('Dossiers')}</span>
                    {allCompanies.length === 0 ? (
                      <p className="pack-editor-modal__hint">{__('Aucun dossier disponible.')}</p>
                    ) : (
                      <div className="pack-editor-modal__company-list">
                        {allCompanies.map((company) => (
                          <label key={company.id} className="pack-editor-modal__company-row">
                            <input
                              type="checkbox"
                              checked={form.targetCompanyIds.includes(company.id)}
                              onChange={() => toggleCompany(company.id)}
                              disabled={saving}
                            />
                            {company.name}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="pack-editor-modal__items-section">
                <div className="pack-editor-modal__items-header">
                  <h4 className="pack-editor-modal__items-title">{__('Diligences')}</h4>
                  <button
                    type="button"
                    className="button button-secondary pack-editor-modal__add-item-btn"
                    onClick={addItem}
                    disabled={saving || itemTargetsLoad !== 'ready' || itemTargets === null}
                  >
                    + {__('Ajouter une diligence')}
                  </button>
                </div>

                {form.items.length === 0 && (
                  <p className="pack-editor-modal__hint">
                    {__('Aucune diligence. Cliquez sur Ajouter pour en créer une.')}
                  </p>
                )}

                <div className="pack-editor-modal__items-list">
                  {form.items.map((item) => {
                    const cat = itemTargets as ItemTargetsPayload;
                    const targetOptions = cat.criteria.map((o) => ({
                      id: o.id,
                      label: o.label ?? `${o.id} — ${o.title}`,
                    }));
                    return (
                      <div key={item.formId} className="pack-item-row">
                        <div className="pack-item-row__fields">
                          <div className="pack-item-row__field-triple">
                            <div className="pack-item-row__field">
                              <label
                                htmlFor={`item-title-${item.formId}`}
                                className="pack-editor-modal__label"
                              >
                                {__('Titre')}<span aria-hidden="true"> *</span>
                              </label>
                              <input
                                id={`item-title-${item.formId}`}
                                type="text"
                                className="pack-editor-modal__input"
                                value={item.title}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  updateItem(item.formId, 'title', e.target.value)
                                }
                                maxLength={191}
                                required
                                disabled={saving}
                              />
                            </div>

                            <div className="pack-item-row__field">
                              <label
                                htmlFor={`item-target-${item.formId}-add`}
                                className="pack-editor-modal__label"
                              >
                                {__('Point de donnée')}<span aria-hidden="true"> *</span>
                              </label>
                              <DisclosureSetPicker
                                id={`item-target-${item.formId}`}
                                values={item.disclosure_ids}
                                onChange={(values) => updateItemDisclosureIds(item.formId, values)}
                                options={targetOptions}
                                disabled={saving}
                              />
                            </div>
                          </div>

                          <div className="pack-item-row__field">
                            <label
                              htmlFor={`item-desc-${item.formId}`}
                              className="pack-editor-modal__label"
                            >
                              {__('Description')}
                            </label>
                            <input
                              id={`item-desc-${item.formId}`}
                              type="text"
                              className="pack-editor-modal__input"
                              value={item.description}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                updateItem(item.formId, 'description', e.target.value)
                              }
                              disabled={saving}
                            />
                          </div>

                          <div className="pack-item-row__field">
                            <label
                              htmlFor={`item-attachments-${item.formId}`}
                              className="pack-editor-modal__label"
                            >
                              {__('Description des pièces jointes')}
                            </label>
                            <textarea
                              id={`item-attachments-${item.formId}`}
                              className="pack-editor-modal__textarea"
                              value={item.attachments_description}
                              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                updateItem(item.formId, 'attachments_description', e.target.value)
                              }
                              rows={2}
                              disabled={saving}
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn-icon pack-item-row__remove-btn"
                          onClick={() => removeItem(item.formId)}
                          aria-label={__('Supprimer la diligence')}
                          disabled={saving}
                        >
                          <TrashRowIcon />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {submitError !== '' && (
                <p className="pack-editor-modal__error" role="alert">
                  {submitError}
                </p>
              )}
            </div>

            <div className="thn-modal__footer">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
                {__('Annuler')}
              </button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? __('Enregistrement…') : __('Enregistrer')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DiligencePackEditorModal;
